import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DocumentArrowDownIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/apiClient';
import { WizardComponentProps } from '../../types';

interface EntityItem {
  Entity: string;
  Object: string;
  Step: number;
  selected: boolean;
  hasMappings: boolean;
}

const ExportConfiguration: React.FC<WizardComponentProps> = ({ isEmbedded }) => {
  const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();

  const [config, setConfig] = useState({
    format: 'csv',
    includeHeaders: true,
    delimiter: ',',
    encoding: 'utf8'
  });

  const [entities, setEntities] = useState<EntityItem[]>([]);
  const [exportResult, setExportResult] = useState<any>(null);

  // Fetch phase entities (the configured entity list for this phase)
  const { data: phaseEntities, isLoading: entitiesLoading } = useQuery({
    queryKey: ['phase-entities', customerId, phaseId],
    queryFn: () => apiClient.getPhaseEntities(customerId!, phaseId!),
    enabled: !!customerId && !!phaseId
  });

  // Fetch V2 mappings to know which entities have mappings configured
  const { data: v2Mappings } = useQuery({
    queryKey: ['entity-mappings-v2', customerId, phaseId],
    queryFn: () => apiClient.getEntityMappingsV2(customerId!, phaseId!),
    enabled: !!customerId && !!phaseId
  });

  // Fetch V1 mappings as fallback
  const { data: v1Mappings } = useQuery({
    queryKey: ['entity-mappings-v1', customerId, phaseId],
    queryFn: () => apiClient.getEntityMappings(customerId!, phaseId!),
    enabled: !!customerId && !!phaseId
  });

  // Build entity list with mapping status
  useEffect(() => {
    if (!phaseEntities) return;

    const mappedEntityNames = new Set<string>();
    if (v2Mappings && Array.isArray(v2Mappings)) {
      v2Mappings.forEach((m: any) => {
        if (m.entityName && (m.fieldMappings?.length > 0 || m.mappings?.length > 0)) {
          mappedEntityNames.add(m.entityName);
        }
      });
    }
    if (v1Mappings && Array.isArray(v1Mappings)) {
      v1Mappings.forEach((m: any) => {
        if (m.entityName && (m.fieldMappings?.length > 0 || m.mappings?.length > 0)) {
          mappedEntityNames.add(m.entityName);
        }
      });
    }

    const items: EntityItem[] = (Array.isArray(phaseEntities) ? phaseEntities : [])
      .filter((e: any) => e.selected !== false)
      .sort((a: any, b: any) => (a.Step || 999) - (b.Step || 999))
      .map((e: any) => ({
        Entity: e.Entity,
        Object: e.Object,
        Step: e.Step,
        selected: mappedEntityNames.has(e.Entity), // Pre-select entities that have mappings
        hasMappings: mappedEntityNames.has(e.Entity)
      }));

    setEntities(items);
  }, [phaseEntities, v2Mappings, v1Mappings]);

  const selectedCount = useMemo(() => entities.filter(e => e.selected).length, [entities]);
  const mappedCount = useMemo(() => entities.filter(e => e.hasMappings).length, [entities]);

  const toggleEntity = (entityName: string) => {
    setEntities(prev => prev.map(e =>
      e.Entity === entityName ? { ...e, selected: !e.selected } : e
    ));
  };

  const selectAll = () => setEntities(prev => prev.map(e => ({ ...e, selected: true })));
  const selectNone = () => setEntities(prev => prev.map(e => ({ ...e, selected: false })));
  const selectMapped = () => setEntities(prev => prev.map(e => ({ ...e, selected: e.hasMappings })));

  const createExportMutation = useMutation({
    mutationFn: () => {
      const selectedEntities = entities.filter(e => e.selected).map(e => e.Entity);
      return apiClient.createExport(customerId!, phaseId!, {
        ...config,
        entityOrder: selectedEntities
      });
    },
    onSuccess: (data) => {
      setExportResult(data);
      toast.success('Export created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Export failed');
    }
  });

  const handleExport = () => {
    if (selectedCount === 0) {
      toast.error('Please select at least one entity to export');
      return;
    }
    createExportMutation.mutate();
  };

  const handleDownload = () => {
    if (exportResult?.downloadUrl) {
      window.open(exportResult.downloadUrl, '_blank');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {!isEmbedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Export Configuration</h1>
          <p className="text-gray-600 mt-2">Configure and generate CSV export for SuccessFactors</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Entity Selection */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">Entities to Export</h2>
            <div className="flex items-center gap-2 text-sm">
              <button onClick={selectAll} className="text-blue-600 hover:text-blue-800 font-medium">All</button>
              <span className="text-gray-300">|</span>
              <button onClick={selectNone} className="text-blue-600 hover:text-blue-800 font-medium">None</button>
              <span className="text-gray-300">|</span>
              <button onClick={selectMapped} className="text-blue-600 hover:text-blue-800 font-medium">Mapped only</button>
            </div>
          </div>

          {entitiesLoading ? (
            <div className="py-4 text-center">
              <div className="loading-spinner inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full" />
              <span className="ml-2 text-gray-500 text-sm">Loading entities...</span>
            </div>
          ) : entities.length === 0 ? (
            <div className="py-4 text-center text-gray-500 text-sm">
              <ExclamationTriangleIcon className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
              No entities configured for this phase. Please select entities in the wizard first.
            </div>
          ) : (
            <>
              <div className="border rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
                {entities.map(entity => (
                  <label
                    key={entity.Entity}
                    className={`flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${entity.selected ? 'bg-blue-50/50' : ''
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={entity.selected}
                      onChange={() => toggleEntity(entity.Entity)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-gray-400 w-5">{entity.Step}.</span>
                        <span className="font-medium text-gray-900 text-sm">{entity.Entity}</span>
                        <span className="text-gray-400 text-xs">({entity.Object})</span>
                      </div>
                    </div>
                    {entity.hasMappings ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                        <CheckCircleIcon className="w-4 h-4" />
                        Mapped
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-yellow-500 font-medium">
                        <ExclamationTriangleIcon className="w-4 h-4" />
                        No mapping
                      </span>
                    )}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {selectedCount} of {entities.length} entities selected · {mappedCount} have mappings configured
              </p>
            </>
          )}
        </div>

        {/* Export Settings */}
        <h2 className="text-xl font-semibold mb-4">Export Settings</h2>
        <div className="space-y-4">
          <div>
            <label className="form-label">Export Format</label>
            <select
              value={config.format}
              onChange={(e) => setConfig({ ...config, format: e.target.value })}
              className="form-input"
            >
              <option value="csv">CSV</option>
              <option value="api">Direct API</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeHeaders"
              checked={config.includeHeaders}
              onChange={(e) => setConfig({ ...config, includeHeaders: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="includeHeaders" className="text-sm text-gray-700">
              Include column headers
            </label>
          </div>

          <div>
            <label className="form-label">Delimiter</label>
            <select
              value={config.delimiter}
              onChange={(e) => setConfig({ ...config, delimiter: e.target.value })}
              className="form-input"
            >
              <option value=",">Comma (,)</option>
              <option value=";">Semicolon (;)</option>
              <option value={"\t"}>Tab</option>
              <option value="|">Pipe (|)</option>
            </select>
          </div>

          <div>
            <label className="form-label">Encoding</label>
            <select
              value={config.encoding}
              onChange={(e) => setConfig({ ...config, encoding: e.target.value })}
              className="form-input"
            >
              <option value="utf8">UTF-8</option>
              <option value="ascii">ASCII</option>
              <option value="latin1">Latin-1</option>
            </select>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={handleExport}
            disabled={createExportMutation.isPending || selectedCount === 0}
            className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
            {createExportMutation.isPending
              ? 'Generating...'
              : `Generate Export (${selectedCount} ${selectedCount === 1 ? 'entity' : 'entities'})`}
          </button>
        </div>

        {exportResult && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-2">Export Ready</h3>
            <p className="text-green-700 mb-2">
              Export completed with {exportResult.totalRecords || exportResult.recordCount || 0} records
            </p>
            {exportResult.files && exportResult.files.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-green-800 mb-1">Generated files:</p>
                <ul className="space-y-1">
                  {exportResult.files.map((f: any, i: number) => (
                    <li key={i} className="text-sm text-green-700 flex items-center gap-2">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="font-medium">{f.fileName}</span>
                      <span className="text-green-600">({f.recordCount} rows)</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={handleDownload}
              className="btn-success"
            >
              Download Export
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportConfiguration;
